import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from './ui/dialog';
import { Button } from './ui/button';
import { Label } from './ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from './ui/select';
import { Input } from './ui/input';
import { Badge } from './ui/badge';
import { useCart } from '../context/CartContext';
import { useToast } from '../hooks/use-toast';
import { ShoppingCart, Ruler, AlertTriangle, XCircle } from 'lucide-react';
import { getColorHex, isLightColor } from '../utils/colorMap';
import { resolveImageUrl, getMainImage } from '../utils/imageUrl';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Color swatch component
const ColorSwatch = ({ color, selected, onClick }) => {
  const hex = getColorHex(color);
  const light = isLightColor(color);
  
  return (
    <button
      type="button"
      onClick={onClick}
      className={`flex items-center gap-2 px-3 py-2 rounded-lg border-2 transition-all ${
        selected 
          ? 'border-black bg-neutral-50' 
          : 'border-gray-200 hover:border-gray-300'
      }`}
    >
      <span 
        className="w-5 h-5 rounded-full flex-shrink-0"
        style={{ 
          backgroundColor: hex,
          border: light ? '1px solid #D1D5DB' : 'none'
        }}
      />
      <span className="text-sm text-gray-700">{color}</span>
    </button>
  );
};

const AddToCartModal = ({ isOpen, onClose, garment }) => {
  const { addToCart } = useCart();
  const { toast } = useToast();
  const [selectedSize, setSelectedSize] = useState('');
  const [selectedColor, setSelectedColor] = useState('');
  const [quantity, setQuantity] = useState(1);
  const [stockInfo, setStockInfo] = useState(null);
  const [loadingStock, setLoadingStock] = useState(false);

  const [stockSizes, setStockSizes] = useState([]);

  // Sizes = product sizes + any size that has stock in inventory (so nothing in stock is hidden)
  const SIZE_ORDER = ['XXS', 'XS', 'S', 'M', 'L', 'XL', 'XXL', 'Única'];
  const baseSizes = garment?.sizes?.length ? garment.sizes : ['XS', 'S', 'M', 'L', 'XL', 'XXL'];
  const sizes = [...new Set([...baseSizes, ...stockSizes])].sort(
    (a, b) => (SIZE_ORDER.indexOf(a) + 1 || 99) - (SIZE_ORDER.indexOf(b) + 1 || 99)
  );

  useEffect(() => {
    if (!isOpen || !garment?.id) return;
    axios.get(`${API}/inventory/garment/${garment.id}`)
      .then(({ data }) => setStockSizes([...new Set((data.stock || []).filter((s) => s.quantity > 0).map((s) => s.size))]))
      .catch(() => setStockSizes([]));
  }, [isOpen, garment?.id]);

  // Check stock when size and color are selected
  useEffect(() => {
    if (garment && selectedSize && selectedColor) {
      checkStock();
    } else {
      setStockInfo(null);
    }
  }, [garment, selectedSize, selectedColor]);

  // Reset state when modal opens/closes
  useEffect(() => {
    if (!isOpen) {
      setSelectedSize('');
      setSelectedColor('');
      setQuantity(1);
      setStockInfo(null);
    }
  }, [isOpen]);

  const checkStock = async () => {
    if (!garment || !selectedSize || !selectedColor) return;
    
    setLoadingStock(true);
    try {
      const response = await axios.get(
        `${API}/inventory/check/${garment.id}/${encodeURIComponent(selectedSize)}/${encodeURIComponent(selectedColor)}`
      );
      setStockInfo(response.data);
    } catch (error) {
      console.error('Error checking stock:', error);
      // If no stock entry exists, assume unlimited (for backwards compatibility)
      setStockInfo(null);
    } finally {
      setLoadingStock(false);
    }
  };

  const handleAddToCart = () => {
    if (!selectedSize) {
      toast({
        title: 'Error',
        description: 'Por favor selecciona una talla',
        variant: 'destructive'
      });
      return;
    }

    if (!selectedColor) {
      toast({
        title: 'Error',
        description: 'Por favor selecciona un color',
        variant: 'destructive'
      });
      return;
    }

    // Check if out of stock
    if (stockInfo && stockInfo.status === 'out_of_stock') {
      toast({
        title: 'Producto agotado',
        description: 'Esta combinación de talla y color está agotada',
        variant: 'destructive'
      });
      return;
    }

    // Check if quantity exceeds available
    if (stockInfo && stockInfo.quantity > 0 && quantity > stockInfo.quantity) {
      toast({
        title: 'Stock insuficiente',
        description: `Solo hay ${stockInfo.quantity} unidades disponibles`,
        variant: 'destructive'
      });
      return;
    }

    addToCart(garment, selectedSize, selectedColor, quantity);
    toast({
      title: '¡Agregado al carrito!',
      description: `${garment.name} - ${selectedSize} - ${selectedColor}`
    });
    onClose();
  };

  const getStockBadge = () => {
    if (!stockInfo) return null;
    
    if (stockInfo.status === 'out_of_stock') {
      return (
        <Badge className="bg-red-100 text-red-700 border-red-300 flex items-center gap-1">
          <XCircle className="w-3 h-3" />
          AGOTADO
        </Badge>
      );
    }
    
    if (stockInfo.status === 'low_stock') {
      return (
        <Badge className="bg-yellow-100 text-yellow-700 border-yellow-300 flex items-center gap-1">
          <AlertTriangle className="w-3 h-3" />
          ¡Últimas {stockInfo.quantity} unidades!
        </Badge>
      );
    }
    
    return null;
  };

  const maxQuantity = stockInfo && stockInfo.quantity > 0 
    ? Math.min(10, stockInfo.quantity) 
    : 10;

  if (!garment) return null;

  const isOutOfStock = stockInfo && stockInfo.status === 'out_of_stock';

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <ShoppingCart className="w-5 h-5" />
            Agregar al carrito
          </DialogTitle>
          <DialogDescription>
            {garment.name} - {garment.price?.toFixed(2)}€
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4 py-4">
          {/* Imagen */}
          <div className="flex justify-center">
            <img
              src={resolveImageUrl(getMainImage(garment))}
              alt={garment.name}
              className="h-32 w-32 object-contain rounded-lg bg-gray-50"
            />
          </div>

          {/* Talla */}
          <div>
            <div className="flex items-center justify-between mb-1">
              <Label htmlFor="size">Talla *</Label>
              <Link 
                to="/guia-tallas" 
                className="text-xs text-black hover:text-black flex items-center gap-1"
                onClick={onClose}
              >
                <Ruler className="w-3 h-3" />
                Guía de tallas
              </Link>
            </div>
            <Select value={selectedSize} onValueChange={setSelectedSize}>
              <SelectTrigger data-testid="select-size">
                <SelectValue placeholder="Selecciona una talla" />
              </SelectTrigger>
              <SelectContent>
                {sizes.map(size => (
                  <SelectItem key={size} value={size}>{size}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          {/* Color */}
          <div>
            <Label htmlFor="color" className="mb-2 block">Color *</Label>
            <div className="flex flex-wrap gap-2">
              {garment.colors && garment.colors.length > 0 ? (
                garment.colors.map(color => (
                  <ColorSwatch
                    key={color}
                    color={color}
                    selected={selectedColor === color}
                    onClick={() => setSelectedColor(color)}
                  />
                ))
              ) : (
                <ColorSwatch
                  color="Color único"
                  selected={selectedColor === "Color único"}
                  onClick={() => setSelectedColor("Color único")}
                />
              )}
            </div>
          </div>

          {/* Stock Status */}
          {loadingStock && (
            <div className="text-sm text-gray-500 text-center">
              Verificando disponibilidad...
            </div>
          )}
          
          {stockInfo && selectedSize && selectedColor && (
            <div className="flex justify-center">
              {getStockBadge()}
            </div>
          )}

          {/* Cantidad */}
          <div>
            <Label htmlFor="quantity">Cantidad</Label>
            <Input
              id="quantity"
              type="number"
              min="1"
              max={maxQuantity}
              value={quantity}
              onChange={(e) => setQuantity(Math.max(1, Math.min(maxQuantity, parseInt(e.target.value) || 1)))}
              disabled={isOutOfStock}
              data-testid="input-quantity"
            />
            {stockInfo && stockInfo.quantity > 0 && stockInfo.quantity <= 10 && (
              <p className="text-xs text-gray-500 mt-1">
                Máximo disponible: {stockInfo.quantity}
              </p>
            )}
          </div>
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={onClose}>Cancelar</Button>
          <Button
            onClick={handleAddToCart}
            disabled={isOutOfStock}
            className={`${
              isOutOfStock 
                ? 'bg-gray-400 cursor-not-allowed' 
                : 'bg-black hover:bg-neutral-800'
            }`}
            data-testid="confirm-add-to-cart"
          >
            {isOutOfStock ? 'Agotado' : 'Agregar al carrito'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default AddToCartModal;
